// @ts-strict-ignore
import * as React from 'react';
import {
    SectionsProps,
    MonitoringSectionFilter,
    ReviewsSection,
    StabilitySection,
    AnalyticsSection,
} from './MonitoringContainer';
import {
    AppStoreAvailableBuild,
    IntegrationType,
    PhasedRelease,
    RolloutMonitoring,
    SubscriptionFeatureType,
} from '@/types';
import { FeatureSection, GroupedMonitoringStatistic } from '@/typescriptHelpers';
import { ClockCircleTwoTone } from '@ant-design/icons';
import Typography from 'antd/lib/typography';
import { antGrey } from '../styles/globalStyles';
import { useThemeMode } from '../styles/themeMode';
import { MonitoringGeneralReleaseInfo } from './MonitoringGeneralReleaseInfo';
import { MonitoringMetricCardsGrid } from './MonitoringMetricCardsGrid';
import Select from 'antd/lib/select';
import { getIsOTAPlatform } from '@/helpers';
import { Content } from 'antd/lib/layout/layout';
import ReleaseFeedbackPrompt from './ReleaseFeedbackPrompt';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';

const selectedMonitoringSection = (props: SectionsProps) => {
    const shouldShowReviews = !props.isRolloutSourcedFromBetaStep && !getIsOTAPlatform(props.platform);

    switch (props.section) {
        case MonitoringSectionFilter.All:
            return (
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
                    {(isDisabled) => {
                        return isDisabled ?
                                <>
                                    {shouldShowReviews && <ReviewsSection {...props} shouldShowDisabledUI={true} />}
                                    <StabilitySection {...props} shouldShowDisabledUI={true} />
                                    <AnalyticsSection {...props} shouldShowDisabledUI={true} />
                                </>
                            :   <>
                                    <StabilitySection {...props} />
                                    <AnalyticsSection {...props} />
                                    {shouldShowReviews && <ReviewsSection {...props} />}
                                </>;
                    }}
                </PlanRestrictedFeatureWrapper>
            );
        case MonitoringSectionFilter.Stability:
            return <StabilitySection {...props} />;
        case MonitoringSectionFilter.Analytics:
            return <AnalyticsSection {...props} />;
        case MonitoringSectionFilter.Reviews:
            return shouldShowReviews ? <ReviewsSection {...props} /> : null;
    }
};

interface RolloutMonitoringSummaryContentProps extends SectionsProps {
    displayWaitingForRelease: boolean;
    releaseVersion: string;
    setSelectedBuild: (build: AppStoreAvailableBuild) => void;
    handleMonitoringSectionFilterChange: (value: string) => void;
    isDemoMode: boolean;
    phasedRelease: PhasedRelease;
    appStoreIntegrationId?: IntegrationType;
    rolloutMonitoring: RolloutMonitoring;
    groupedMonitoringStatisticsForCards: GroupedMonitoringStatistic[];
    isMultiApk: boolean;
    isRolloutSourcedFromBetaStep?: boolean;
}

export const RolloutMonitoringSummaryContent: React.FC<RolloutMonitoringSummaryContentProps> = (
    props: RolloutMonitoringSummaryContentProps,
) => {
    const { mode } = useThemeMode();
    const isDarkMode = mode === 'dark';
    const isReleaseNotReady = !props.isReleaseFromScraper && props.displayWaitingForRelease;
    if (isReleaseNotReady) {
        return (
            <>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            maxWidth: 435,
                            backgroundColor: antGrey.grey2,
                            borderRadius: 8,
                            padding: 30,
                            display: 'flex',
                            gap: 25,
                        }}
                    >
                        <ClockCircleTwoTone
                            twoToneColor={isDarkMode ? [antGrey.grey5, 'transparent'] : antGrey.grey5}
                            style={{ fontSize: 28, paddingTop: 4 }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Typography.Text
                                style={{
                                    fontSize: 24,
                                    fontWeight: 600,
                                    color: antGrey.grey8,
                                }}
                            >
                                Waiting for release
                            </Typography.Text>
                            <Typography.Text style={{ color: antGrey.grey8 }}>
                                Check back here after your update is released!
                            </Typography.Text>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <MonitoringGeneralReleaseInfo
                        releaseId={props.releaseId}
                        selectedBuild={props.selectedBuild}
                        appStoreSelectedBuilds={props.appStoreSelectedBuilds}
                        setSelectedBuild={props.setSelectedBuild}
                        releasedAt={props.releasedAt}
                    />
                    <div>
                        <Select
                            defaultValue={MonitoringSectionFilter.All}
                            style={{ width: 300 }}
                            onChange={props.handleMonitoringSectionFilterChange}
                        >
                            <Select.Option value={MonitoringSectionFilter.All} key={MonitoringSectionFilter.All}>
                                All metric types
                            </Select.Option>
                            <Select.Option
                                value={MonitoringSectionFilter.Stability}
                                key={MonitoringSectionFilter.Stability}
                            >
                                Stability
                            </Select.Option>
                            <Select.Option
                                value={MonitoringSectionFilter.Analytics}
                                key={MonitoringSectionFilter.Analytics}
                            >
                                Observability & analytics
                            </Select.Option>
                            {!props.isRolloutSourcedFromBetaStep && !getIsOTAPlatform(props.platform) && (
                                <Select.Option
                                    value={MonitoringSectionFilter.Reviews}
                                    key={MonitoringSectionFilter.Reviews}
                                >
                                    Reviews
                                </Select.Option>
                            )}
                        </Select>
                    </div>
                </div>
            </div>
            <Content style={{ paddingTop: 20, paddingBottom: 80, gap: 16 }}>
                {!props.isReleaseFromScraper && !props.isDemoMode && !props.isRolloutSourcedFromBetaStep && (
                    <ReleaseFeedbackPrompt {...props} />
                )}
                <EmptyStateWrapper
                    appId={props.appId}
                    featureSections={[FeatureSection.RolloutTopSummary]}
                    releaseId={props.releaseId}
                    shouldCenterContent
                >
                    {(isInEmptyState) => (
                        <div>
                            <MonitoringMetricCardsGrid
                                isAutomatedPhasedRollout={props.isAutomatedPhasedRollout}
                                stepRunComponentProps={null}
                                phasedRelease={props.phasedRelease}
                                platform={props.platform}
                                appStoreType={props.appStoreType}
                                stabilityIntegrationId={props.stabilityIntegrationType}
                                appStoreIntegrationId={props.appStoreIntegrationId}
                                {...props}
                                selectedSection={props.section}
                                rolloutMonitoring={props.rolloutMonitoring}
                                selectedBuild={props.selectedBuild}
                                isLoading={props.isLoading || isInEmptyState}
                                groupedMonitoringStatistics={props.groupedMonitoringStatisticsForCards}
                                isRolloutSourcedFromBetaStep={props.isRolloutSourcedFromBetaStep}
                                rolloutStartedAt={props.rolloutStartedAt}
                                isPhased={props.isPhased}
                                calculatedDataPerSection={props.calculatedDataPerSection}
                                isMultiApk={props.isMultiApk}
                                releaseVersion={props.releaseVersion}
                                disableLoadShimmer={isInEmptyState}
                                autoHaltUnstablePhasedReleasesAutomationId={
                                    props.autoHaltUnstablePhasedReleasesAutomationId
                                }
                                autoPromoteStablePhasedReleasesAutomationId={
                                    props.autoPromoteStablePhasedReleasesAutomationId
                                }
                                autoStagedRolloutAutomationId={props.autoStagedRolloutAutomationId}
                            />
                        </div>
                    )}
                </EmptyStateWrapper>

                <Divider
                    dashed
                    style={{
                        marginBottom: 0,
                    }}
                />
                <Space direction="vertical" style={{ width: '100%', paddingBottom: 40 }}>
                    {selectedMonitoringSection({
                        ...props,
                    })}
                </Space>
            </Content>
        </>
    );
};
